/**
 * The organisation's design system on the console itself, when an administrator asks for it: the
 * `design-system` entry marked `consoleUse` has its tokens stylesheet loaded after the console's
 * own (a <style> appended last in <head>), so the console wears the organisation's colours where
 * its selectors match (`:root`, `.dark`, `[data-theme=…]`). Opt-in and reversible from the Design
 * systems page; without one the console is in the Mojo Up design language, its own. `@import`
 * lines are dropped: they belong to a build, and nothing is fetched from elsewhere.
 *
 * Trying a system on (this browser only, until reload) wears more: since round 4 its art stylesheet,
 * its asset stylesheets and its fonts as well as the tokens.
 */

import type { CatalogueEntry, DesignSystemContent } from '@aioe/shared';

import { fetchAsset, routes } from './api/client.ts';

const STYLE_ID = 'aioe-organisation-design-system';

export interface ConsoleDesignSystem {
  /** The entry's id and the system's name, when the console wears the organisation's; undefined for its own. */
  id?: string;
  name?: string;
}

let current: ConsoleDesignSystem = {};
const listeners = new Set<() => void>();

export const consoleDesignSystem = (): ConsoleDesignSystem => current;
export function subscribeConsoleDesignSystem(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** The stylesheet as the console loads it: without `@import` lines. */
export const withoutImports = (css: string) => css.replace(/@import\s+[^;]+;/g, '');

/** Applies the entry the console should wear (or none), now. */
export function applyConsoleDesignSystem(entry: CatalogueEntry | undefined): void {
  const c = entry?.content as DesignSystemContent | undefined;
  const css = c ? entry?.files?.[c.tokens] : undefined;
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!entry || !c || css === undefined) {
    style?.remove();
    current = {};
  } else {
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
    }
    style.textContent = withoutImports(css);
    // Last in <head>, after the console's own stylesheets, every time (a dev reload adds more).
    document.head.appendChild(style);
    current = { id: entry.id, name: c.name };
    // A design system being tried on stays on top.
    const tried = document.getElementById('aioe-design-system-try-on');
    if (tried) document.head.appendChild(tried);
  }
  for (const l of listeners) l();
}

/** Reads the catalogue's design systems and wears the one marked for the console; quiet on failure. */
export async function loadConsoleDesignSystem(): Promise<void> {
  try {
    const cat = await routes.designSystemCatalogue();
    applyConsoleDesignSystem(cat.entries.find((e) => e.kind === 'design-system' && (e.content as Partial<DesignSystemContent> | null)?.consoleUse));
  } catch {
    /* an older platform, or not signed in yet: the console stays as it is */
  }
}

const TRY_ID = 'aioe-design-system-try-on';

export interface TryingOn {
  id: string;
  name: string;
  version: string;
  /** What is worn, in words: "the tokens, the art and 2 fonts". */
  wearing: string;
  /** Fonts or stylesheets that could not be fetched, by path. */
  missing: string[];
}

let trying: TryingOn | undefined;
/** Each try-on is numbered, so a slow one finishing after a newer one (or after Stop) changes nothing. */
let attempt = 0;

/** The design system this browser is trying on, until it reloads; nobody else sees it. */
export const tryingOn = (): TryingOn | undefined => trying;

/** A reference in a stylesheet at `from`, resolved to a path in the system (`../fonts/a.woff2` from `css/art.css` is `fonts/a.woff2`). */
function resolveRef(from: string, ref: string): string | undefined {
  const parts = from.split('/').slice(0, -1);
  for (const seg of ref.split(/[?#]/)[0]!.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (!parts.length) return undefined;
      parts.pop();
    } else parts.push(seg);
  }
  return parts.join('/');
}

async function dataUrl(blob: Blob, type: string): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${blob.type || type};base64,${btoa(s)}`;
}

/** Fonts tried on, at most, altogether: a try-on is a look, not a download. */
const TRY_FONT_BYTES = 16 * 1024 * 1024;

/**
 * Everything a design system wears, as one stylesheet: an `@font-face` for each font file (as a
 * `data:` address, fetched with the bearer), then the tokens, the art and the asset stylesheets in
 * their load order, `@import` lines dropped and each `url()` that names one of the system's own
 * fonts or pictures pointed at its `data:` address. A `url()` to anything else is emptied: the
 * console fetches nothing a design system names from elsewhere.
 */
export async function designSystemTryOnCss(entry: CatalogueEntry, fetchBlob: (assetId: string) => Promise<Blob>): Promise<{ css: string; wearing: string; missing: string[] }> {
  const c = entry.content as DesignSystemContent;
  const files = entry.files ?? {};
  const missing: string[] = [];
  const byPath = new Map<string, string>();
  let fontBytes = 0;
  let fontFiles = 0;
  for (const f of c.fonts ?? [])
    for (const x of f.files) {
      try {
        const blob = await fetchBlob(x.asset);
        if (fontBytes + blob.size > TRY_FONT_BYTES) throw new Error('too much');
        fontBytes += blob.size;
        byPath.set(x.path, await dataUrl(blob, 'font/woff2'));
        fontFiles++;
      } catch {
        missing.push(x.path);
      }
    }
  for (const l of c.logos ?? []) {
    try {
      byPath.set(l.path, await dataUrl(await fetchBlob(l.asset), 'image/png'));
    } catch {
      /* a logo the stylesheets may not even name */
    }
  }
  const sheets: Array<{ path: string; css: string }> = [];
  if (files[c.tokens] !== undefined) sheets.push({ path: c.tokens, css: files[c.tokens]! });
  if (c.art && files[c.art] !== undefined) sheets.push({ path: c.art, css: files[c.art]! });
  for (const s of c.stylesheets ?? []) {
    try {
      sheets.push({
        path: s.path,
        css: await (await fetchBlob(s.asset)).text(),
      });
    } catch {
      missing.push(s.path);
    }
  }
  const relink = (css: string, at: string) =>
    withoutImports(css).replace(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi, (m, _q: string, ref: string) => {
      const r = ref.trim();
      if (/^data:/i.test(r) || r.startsWith('#')) return m;
      const target = /^[a-z][a-z0-9+.-]*:|^\/\//i.test(r) || r.startsWith('/') ? undefined : resolveRef(at, r);
      const data = target ? byPath.get(target) : undefined;
      return data ? `url("${data}")` : 'url("data:,")';
    });
  const faces = (c.fonts ?? []).flatMap((f) =>
    f.files
      .filter((x) => byPath.has(x.path))
      .map(
        (x) =>
          `@font-face{font-family:${JSON.stringify(f.name)};src:url("${byPath.get(x.path)}");${x.weight ? `font-weight:${x.weight};` : ''}${x.style ? `font-style:${x.style};` : ''}font-display:swap}`,
      ),
  );
  const pieces = [
    'the tokens',
    ...(c.art && files[c.art] !== undefined ? ['the art'] : []),
    ...((c.stylesheets ?? []).length ? [`${c.stylesheets!.length} more ${c.stylesheets!.length === 1 ? 'stylesheet' : 'stylesheets'}`] : []),
    ...(fontFiles ? [`${c.fonts!.length} ${c.fonts!.length === 1 ? 'font' : 'fonts'}`] : []),
  ];
  const wearing = pieces.length > 1 ? `${pieces.slice(0, -1).join(', ')} and ${pieces[pieces.length - 1]}` : pieces[0]!;
  return {
    css: [...faces, ...sheets.map((x) => relink(x.css, x.path))].join('\n'),
    wearing,
    missing,
  };
}

/**
 * "Try it on" (design systems rounds 2 and 4): wears one version's tokens, art stylesheet, asset
 * stylesheets and fonts after everything else, in this browser only and until it reloads, without
 * publishing anything. Undefined stops.
 */
export function tryOnDesignSystem(entry: CatalogueEntry | undefined): void {
  void wearDesignSystem(entry);
}

/** {@link tryOnDesignSystem}, finished when the fonts and stylesheets are fetched and worn. Never throws: what could not be fetched is listed in `missing`. */
export async function wearDesignSystem(entry: CatalogueEntry | undefined, fetchBlob: (assetId: string) => Promise<Blob> = fetchAsset): Promise<void> {
  const mine = ++attempt;
  const c = entry?.content as DesignSystemContent | undefined;
  const found = entry && c && entry.files?.[c.tokens] !== undefined ? await designSystemTryOnCss(entry, fetchBlob) : undefined;
  if (mine !== attempt) return;
  let style = document.getElementById(TRY_ID) as HTMLStyleElement | null;
  if (!entry || !c || !found) {
    style?.remove();
    trying = undefined;
  } else {
    if (!style) {
      style = document.createElement('style');
      style.id = TRY_ID;
    }
    style.textContent = found.css;
    // After the organisation's own (when the console wears one), so the one tried wins.
    document.head.appendChild(style);
    trying = {
      id: entry.id,
      name: c.name,
      version: c.version,
      wearing: found.wearing,
      missing: found.missing,
    };
  }
  for (const l of listeners) l();
}
